import { Image, Label, Skeleton, Text, TextSize, TextWeight } from '@vizit/atoms';
import { cva, cx } from '@vizit/theme';
import type { ReactNode } from 'react';
import { Radio, RadioGroup, type RadioGroupProps } from 'react-aria-components';

export interface RadioButtonProps {
  /**
   * The value of the radio button
   * @type {string}
   */
  value: string;
  /**
   * The title of the radio button
   * @type {string}
   */
  title: string;
  /**
   * The subtitle of the radio button
   * @type {string}
   * @optional
   */
  subtitle?: string;
  /**
   * The image source URL for the radio button
   * @type {string}
   * @optional
   */
  imageSrc?: string;
  /**
   * Whether this radio button is designed to have an image
   * Used to distinguish between "no image by design" and "image not loaded yet"
   * @type {boolean}
   * @optional
   * @default false
   */
  hasImage?: boolean;
  /**
   * The image alt text
   * @type {string}
   */
  imageAlt: string;
  /**
   * Whether the radio button is disabled
   * @type {boolean}
   * @optional
   * @default false
   */
  isDisabled?: boolean;
  /**
   * Whether the image is in loading state
   * @type {boolean}
   * @optional
   * @default false
   */
  isImageLoading?: boolean;
  /**
   * Fallback image source URL if the primary image fails to load
   * @type {string}
   * @optional
   */
  fallbackImageSrc?: string;
  /**
   * Whether the radio button group is in a loading state
   * @type {boolean}
   * @optional
   * @default false
   */
  isLoading?: boolean;
}

export interface RadioButtonGroupProps extends Omit<RadioGroupProps, 'children'> {
  /**
   * The radio button options
   * @type {RadioButtonProps[]}
   */
  options: RadioButtonProps[];
  /**
   * Whether the radio button group is in a loading state
   * @type {boolean}
   * @optional
   * @default false
   */
  isLoading?: boolean;
  /**
   * Custom class name for the radio button group
   * @type {string}
   * @optional
   */
  className?: string;
  /**
   * Callback when a radio button is selected
   * @type {function}
   * @optional
   */
  onChange?: (value: string) => void;
  /**
   * The label for the radio button group
   * Please provide either a label or 'aria-label' prop for accessibility
   * @type {string}
   * @optional
   */
  label?: string;
}

const styles = {
  radioButtonGroup: cva({
    base: 'flex flex-col gap-8',
  }),
  radioButton: cva({
    base: 'flex relative cursor-pointer border bg-primary overflow-hidden transition-all hover:bg-primary-hover',
    variants: {
      isSelected: {
        // We're using the box-shadow for the focused and selected states to avoid a content shift when the radio button is focused
        true: 'shadow-radio-button-selected',
        false: 'border border-secondary',
      },
      isDisabled: {
        true: 'cursor-not-allowed bg-disabled-subtle',
      },
      isFocused: {
        // Same deal with the shadow as with the selected state
        true: 'shadow-radio-button-focused',
      },
    },
    compoundVariants: [
      {
        isSelected: true,
        isDisabled: true,
        // Same deal with the shadow as with the focused, selected state
        className: 'shadow-radio-button-selected-disabled border-0',
      },
    ],
  }),
  radioContent: cva({
    base: 'flex w-full',
  }),
  imageContainer: cva({
    base: 'w-[114px] h-[114px] flex-shrink-0',
  }),
  content: cva({
    base: 'flex flex-col justify-start flex-grow p-8 gap-4',
  }),
  title: cva({
    base: 'text-secondary-900',
    variants: {
      isDisabled: {
        true: 'text-disabled',
      },
    },
  }),
  subtitle: cva({
    base: 'text-secondary-900',
    variants: {
      isDisabled: {
        true: 'text-disabled',
      },
    },
  }),
};

const RadioButton = ({
  value,
  title,
  subtitle,
  imageSrc,
  fallbackImageSrc,
  imageAlt,
  isDisabled,
  isLoading,
  hasImage: hasImageProp = false,
  isImageLoading = false,
}: RadioButtonProps): ReactNode => {
  const hasImage = !!hasImageProp || !!imageSrc;
  const isImageBroken = !isLoading && !isImageLoading && !imageSrc;

  // Radio button should be disabled if it's explicitly disabled or if it has an image that's still loading
  const isButtonDisabled = isDisabled || (hasImage && isImageLoading);

  return (
    <Radio
      value={value}
      isDisabled={isButtonDisabled}
      className={({ isSelected, isFocusVisible, isDisabled: disabled }) =>
        cx(styles.radioButton({ isSelected, isDisabled: disabled, isFocused: isFocusVisible }))
      }
    >
      {({ isDisabled: disabled }) => (
        <div className={styles.radioContent()}>
          {/* Render the image area if it has an image by design or if there's an actual image */}
          {hasImage && (
            <div className={styles.imageContainer()}>
              {isImageLoading ? (
                <Skeleton aria-label="Image loading" className="w-full h-full" variant="square" />
              ) : (
                <Image
                  isThumbnail
                  isBroken={isImageBroken}
                  imageClassName=""
                  src={imageSrc ?? null}
                  fallbackSrc={fallbackImageSrc}
                  name={imageAlt}
                  shape="square"
                />
              )}
            </div>
          )}
          <div className={styles.content()}>
            <Text size={TextSize.Xs} weight={TextWeight.Medium} className={styles.title({ isDisabled: disabled })}>
              {title}
            </Text>
            {subtitle && (
              <Text
                size={TextSize.Xs}
                showEllipsis={false}
                weight={TextWeight.Regular}
                className={styles.subtitle({ isDisabled: disabled })}
              >
                {subtitle}
              </Text>
            )}
          </div>
        </div>
      )}
    </Radio>
  );
};

export const RadioButtonGroup = ({
  options,
  className,
  onChange,
  label,
  isLoading,
  ...props
}: RadioButtonGroupProps) => {
  const handleChange = (value: string) => {
    // Only trigger onChange if the option is not disabled
    const selectedOption = options.find((option) => option.value === value);
    if (!selectedOption?.isDisabled) {
      onChange?.(value);
    }
  };

  return (
    <RadioGroup {...props} className={cx(styles.radioButtonGroup(), className)} onChange={handleChange}>
      {label && <Label>{label}</Label>}
      {options.map((option) => (
        <RadioButton key={option.value} {...option} isLoading={isLoading} />
      ))}
    </RadioGroup>
  );
};
